<script setup lang="ts" generic="T extends Record<string, any>">
const defaultsStore = useDefaultsStore();
const device = useDevice();

const props = defineProps<{ items: T, isParallaxLocked: boolean }>();
const parentRef = useTemplateRef('parentRef');
const containerRef = useTemplateRef('containerRef');
const mobileRef = useTemplateRef('mobileRef');
const parentRefOffset = useElementBounding(parentRef);
const containerRefOffset = useElementBounding(containerRef);
const mobileRefOffset = useElementBounding(mobileRef);
const topIndicate = 30;
const bottomIndicate = 50;

const isTransformMobile = computed(() => {
    if (!device.isDesktop.value) return;
    return parentRefOffset.top.value <= defaultsStore.headerHeight;
});

const startFixing = computed(() => {
    if (!device.isDesktop.value || !parentRefOffset.height.value) return;
    return parentRefOffset.top.value <= -topIndicate;
});

const endFixing = computed(() => {
    if (!device.isDesktop.value) return;
    return parentRefOffset.bottom.value < containerRefOffset.height.value + topIndicate + bottomIndicate + 40;   
});

const activeIndex = computed(() => {
    if (props.isParallaxLocked) return 0;
    const { top } = parentRefOffset;
    const quarter = parentRefOffset.height.value / 6;

    if (top.value + quarter > 0) return 0;
    if (top.value + (quarter * 2) > 0) return 1;
    if (top.value + (quarter * 3) > 0) return 2;
    if (top.value + (quarter * 4) > 0) return 3;
    return 3;
});

defineExpose({ isTransformMobile });
</script>

<template>
    <SectionsBase ref="parentRef" class="p-0! h-800 overflow-hidden relative">
      <div 
        ref="containerRef" 
        class="w-full flex-center mt-25 before:content-[''] before:absolute before:w-1/2 before:aspect-square 
            before:bg-primary-100 before:blur-3xl before:rounded-full"
        :class="[
            startFixing && !endFixing && 'fixed right-0',
            endFixing && 'absolute!'
        ]"
        :style="{ 
            height: mobileRefOffset.height.value + 'px',
            top: startFixing && !endFixing ? `-${ topIndicate }px` : 'auto',
            bottom: endFixing ? `${ bottomIndicate }px` : 'auto',
        }"
    >
        <div class="w-full h-full container relative flex-center">
            <div
                ref="mobileRef" 
                class="absolute flex-center z-5 transition-transform duration-700"
                :class="isTransformMobile ? 'translate-y-0' : '-translate-y-[calc(100%+100px)]'"
            >
                <IconsMobile class="relative w-70 2xl:w-85 z-2" />
                <div class="absolute flex-center">
                    <Transition name="fade" mode="out-in">
                        <img
                            :key="activeIndex"
                            :src="props.items[activeIndex]?.img"
                            :alt="props.items[activeIndex]?.title"
                            width="300"
                            loading="lazy"
                            class="rounded-4xl w-65 2xl:w-79 z-1"
                        />
                    </Transition>
                </div>
            </div>
            <Transition name="fade-up" mode="out-in">
                <div :key="activeIndex" class="w-full flex-center gap-90 2xl:gap-110 relative z-1">
                    <div 
                        v-for="item, index in props.items[activeIndex]?.items"
                        :key="item.title"
                        class="max-w-105 bg-white rounded-3xl p-4 shadow-gray-m"
                        :class="+index % 2 ? 'rounded-tr-none' : 'rounded-tl-none'"
                    >
                        <div class="flex items-center gap-4 mb-4">
                            <Icon 
                                :name="item.icon" 
                                size="32"
                                :class="index ? 'text-teal-500' : 'text-pink-500'"
                            />
                            <h3>{{ item.title }}</h3>
                        </div>
                        <p class="text-body1-semibold text-gray-500" v-html="item.desc" />
                    </div>
                </div>
            </Transition>
        </div>
      </div>
    </SectionsBase>
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
    transition: 0.3s;
}

.fade-up-enter-active,
.fade-up-leave-active {
  transition: all 0.3s ease;
}

.fade-up-enter-from,
.fade-up-leave-to {
  opacity: 0;
  transform: translateY(50px);
}

.fade-up-enter-to,
.fade-up-leave-from {
  opacity: 1;
  transform: translateY(0);
}
</style>